/**
 * Sessions: the machine's own sessions by the names the desktop shows, and the one open, live (D282): what waits on
 * you answered in place, the conversation as it happens, and the composer at its foot. Open one and carry on; there is
 * no separate remote mode. The open session follows `sessions.subscribe` (a long poll the machine answers when
 * something happens), so nothing here polls quickly. Only the session's own person sends, interrupts and approves; an
 * administrator may watch. When remote control is off for a session, or someone else holds it, one line says so.
 */

import { controllerWords } from '@aioe/shared';
import { ArrowLeft, Comments } from '@gravity-ui/icons';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAuth } from '../../auth/AuthProvider.tsx';
import { connectionMark, LiveConversation, SessionMenu } from '../../components/live/LiveParts.tsx';
import { clockWords, parseLiveKey, statusMark } from '../../components/live/liveModel.ts';
import { useLiveSession } from '../../components/live/useLiveSession.ts';
import { ArtEmpty, ConfirmDialog, ErrorNote, StatusMark } from '../../components/ui.tsx';
import { useNow } from '../../hooks.ts';
import { useMachine } from '../machineContext.ts';
import type { RegisteredWorkspace } from '../projectsModel.ts';
import { useMachineWork, type MachineSession } from '../useMachineWork.ts';
import type { Workbench } from '../useWorkbench.ts';
import { RemoteView } from './RemoteView.tsx';

const tone = 'teal' as const;

const wide = () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches;

export function SessionsView({ w }: { w: Workbench }) {
  const m = useMachine();
  const work = useMachineWork(w);
  const [params, setParams] = useSearchParams();
  const sessions = work.data?.sessions ?? [];
  const asked = params.get('session') ?? undefined;
  // Wide, the first session opens at once so the composer is a step away; narrow, the list comes first.
  const [auto] = useState(wide);
  const key = asked ?? (auto ? sessions[0]?.key : undefined);
  const open = sessions.find((s) => s.key === key) ?? (key ? ({ key, title: 'Session', line: '', state: 'idle', words: '', waiting: 0 } satisfies MachineSession) : undefined);
  const choose = (k: string | undefined) => {
    const next = new URLSearchParams(params);
    if (k) next.set('session', k);
    else next.delete('session');
    setParams(next, { replace: !asked });
  };

  if (work.data && !sessions.length && !asked)
    return (
      <RemoteView narrow>
        <ArtEmpty tone={tone} icon={<Comments />} title={w.connected ? 'No sessions right now' : `${m.subject} is not connected`}>
          {w.connected ? 'Chats, research and team work started on this machine show here, ready to carry on.' : 'Its sessions show here while it runs. What was open to remote control shows as soon as it reports in.'}
        </ArtEmpty>
      </RemoteView>
    );

  return (
    <RemoteView fill>
      <ErrorNote error={work.error} />
      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[17rem_minmax(0,1fr)]">
        <ul className={`m-0 list-none flex-col gap-1 overflow-y-auto p-0 ${open ? 'hidden lg:flex' : 'flex'}`} aria-label="Sessions">
          {!work.data ? <li className="px-3 py-2 text-sm text-muted">Asking {m.name} for its sessions…</li> : null}
          {sessions.map((s) => {
            const current = s.key === open?.key;
            return (
              <li key={s.key}>
                <button
                  type="button"
                  onClick={() => choose(s.key)}
                  aria-current={current || undefined}
                  className={`w-full cursor-[var(--cursor-interactive)] rounded-lg px-3 py-2 text-left ${current ? 'bg-surface shadow-sm' : 'hover:bg-surface-secondary'}`}
                >
                  <span className="block truncate text-sm font-medium">{s.title}</span>
                  <span className="mt-0.5 flex min-w-0 items-center gap-2 text-xs text-muted">
                    <StatusMark state={s.waiting ? 'medium' : s.state} label={s.waiting ? 'Needs you' : s.words} />
                    {s.line ? <span className="min-w-0 truncate">{s.line}</span> : null}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        <div className={`min-h-0 min-w-0 flex-col ${open ? 'flex' : 'hidden lg:flex'}`}>
          {open ? (
            <SessionPane key={open.key} w={w} s={open} onBack={() => choose(undefined)} />
          ) : (
            <div className="flex h-full items-center justify-center rounded-xl bg-surface-secondary p-6 text-sm text-muted">Choose a session to open it.</div>
          )}
        </div>
      </div>
    </RemoteView>
  );
}

/** One session, live: the title line and its one menu, then the conversation as the workbench draws it, the composer docked at its foot. */
function SessionPane({ w, s, onBack }: { w: Workbench; s: MachineSession; onBack: () => void }) {
  const m = useMachine();
  const { account } = useAuth();
  const now = useNow();
  const target = parseLiveKey(s.key);
  const d = w.device.data;
  const person = s.remote?.person ?? (d?.kind === 'node' ? undefined : d?.userId);
  const canControl = Boolean(person && account?.id && person === account.id);
  // The project its repository is registered to on this machine, for a plan's Wrap-up card in the conversation.
  const rootId = s.remote?.rootId ?? s.rootId;
  const projectId = rootId ? (d?.workspaces as RegisteredWorkspace[] | undefined)?.find((x) => x.id === rootId)?.projectId : undefined;
  const live = useLiveSession(w.deviceId, target, { workspace: s.rootId, workbench: m.subject });
  const { state, connection } = live;
  const remote = state.remote;
  const [busy, setBusy] = useState<string>();
  const [problem, setProblem] = useState<string>();
  const [confirmEnd, setConfirmEnd] = useState(false);
  const run = async (what: string, fn: () => Promise<string | undefined>) => {
    setBusy(what);
    setProblem(undefined);
    const p = await fn();
    setBusy(undefined);
    setProblem(p);
    return !p;
  };
  const reachable = connection === 'live';
  const remoteOff = remote?.on === false;
  const mark = connectionMark(connection) ?? statusMark(state.status, state.words);
  const by = remote?.controlledBy;
  // One line about who holds it: someone else, nobody (off), or nothing to say.
  const holder = by
    ? `Being controlled from ${controllerWords(by.name, by.via)}${clockWords(by.at, now) ? ` since ${clockWords(by.at, now)}` : ''}. Sending from here carries on in the same session.`
    : remoteOff
      ? 'This session is under the desktop’s control. Its person turns remote control on in AI Workbench to carry on here.'
      : connection === 'offline'
        ? `${m.subject} is not reachable. What you saw stays here, and it carries on when the machine answers.`
        : undefined;

  return (
    <div className="glass-card flex min-h-0 flex-1 flex-col rounded-xl">
      <div className="flex min-w-0 items-start gap-3 border-b border-separator px-4 py-3">
        <button type="button" aria-label="All sessions" title="All sessions" onClick={onBack} className="mt-0.5 flex size-7 shrink-0 cursor-[var(--cursor-interactive)] items-center justify-center rounded-lg text-muted hover:bg-surface-secondary hover:text-foreground lg:hidden">
          <ArrowLeft className="size-4" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="m-0 truncate text-sm font-semibold">{s.title}</p>
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 text-xs text-muted">
            <StatusMark state={mark.state} label={mark.label} />
            {s.line ? <span className="min-w-0 truncate">{s.line}</span> : null}
          </div>
          {holder ? <p className="m-0 mt-1 text-xs text-muted">{holder}</p> : null}
        </div>
        {canControl ? (
          <SessionMenu remoteOn={remote?.on === true} reachable={reachable && !busy} agentSession={Boolean(target?.sessionId)} onStopRemote={() => void run('remote', live.endRemote)} onEnd={() => setConfirmEnd(true)} />
        ) : null}
      </div>
      {problem ? (
        <div className="px-4 pt-3">
          <ErrorNote error={problem} />
        </div>
      ) : null}
      <LiveConversation live={live} canControl={canControl} me={account?.id} mode={s.remote?.mode} projectId={projectId} canPause={m.scopes.has('tasks:write')} runtimes={d?.facts?.runtimes} fill />
      <ConfirmDialog
        isOpen={confirmEnd}
        title="End this agent session?"
        confirmLabel="End the session"
        danger
        onCancel={() => setConfirmEnd(false)}
        onConfirm={async () => {
          await run('stop', live.stop);
          setConfirmEnd(false);
        }}
      >
        <p className="m-0">The agent stops on {m.name}. What it has done so far stays. To stop only what it is doing now, use Stop beside the message box.</p>
      </ConfirmDialog>
    </div>
  );
}
